<template>
  <div class="app">
    <van-nav-bar class="bank-account-nav" :fixed="true" left-arrow placeholder :title="$t('bankAccount.title')"
      @click-left="onClickLeft" />

    <div class="bank-account-content">
      <div class="bank-account-fields">
        <div class="field-label">
          {{ $t("bankAccount.accountName") }}
        </div>
        <input id="account-name" v-model.trim="formData.name" class="form-control" type="text"
          :placeholder="$t('bankAccount.accountNamePlaceholder')" />

        <label for="bank" class="field-label bank-label">
          {{ $t("bankAccount.bank") }}
        </label>
        <input id="bank" v-model.trim="formData.bank" class="form-control" type="text"
          :placeholder="$t('bankAccount.bankPlaceholder')" />

        <div class="field-label account-number-label">
          {{ $t("bankAccount.accountNumber") }}
        </div>
        <input id="account-number" v-model.trim="formData.card" class="form-control" type="text"
          :placeholder="$t('bankAccount.accountNumberPlaceholder')" />
      </div>

      <div class="default-setting" @click="setAsDefault = !setAsDefault">
        <div :class="['default-setting__check', { checked: setAsDefault }]" aria-hidden="true">
          <svg v-if="setAsDefault" viewBox="0 0 24 24">
            <path d="M5 12.5l4.2 4L19 7" />
          </svg>
        </div>
        <div>{{ $t("bankAccount.setDefault") }}</div>
      </div>

      <van-button class="confirm-button" block native-type="button" :loading="loading" :disabled="loading"
        @click="submit">
        {{ $t("Button.confirm") }}
      </van-button>
    </div>

  </div>
</template>

<script>
import { COMMIT_USER_BANK_INFO, GET_USER_BANK_INFO } from "@/api";
import { Button, NavBar, Toast } from "vant";

export default {
  components: {
    [Button.name]: Button,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      formData: {
        bank: "",
        card: "",
        name: "",
      },
      loading: false,
      setAsDefault: true,
    };
  },
  async mounted() {
    if (this.$route.query.id) {
      await this.loadBankInfo();
    }
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    async loadBankInfo() {
      const routeId = this.$route.query.id;
      if (!routeId) return;

      Toast.loading();
      try {
        const response = await GET_USER_BANK_INFO();
        const list = response.data.data || [];
        if (response.data.ret !== 1 || !list.length) return;

        const selected = list.find(
          (item) =>
            String(item.id) === String(routeId) &&
            (!item.open || item.open === "bank")
        );

        if (selected) {
          this.formData = {
            ...this.formData,
            ...selected,
          };
        }
      } catch (error) {
        console.error("Failed to load bank information:", error);
      } finally {
        Toast.clear();
      }
    },
    async submit() {
      if (!this.formData.bank || !this.formData.card || !this.formData.name) {
        Toast(this.$t("cardToast"));
        return;
      }

      return this.$submitOnce("add-bank-card", async () => {
        this.loading = true;
        Toast.loading();
        try {
          const response = await COMMIT_USER_BANK_INFO({
            ...this.formData,
            bank: this.formData.bank,
            default: this.setAsDefault ? 1 : 0,
          });
          Toast.clear();
          Toast(response.data.msg);
          if (response.data.ret === 1) {
            this.$cookie.set(
              "selectBankInfo",
              JSON.stringify(response.data.data)
            );
            await new Promise((resolve) => setTimeout(resolve, 1500));
            this.$router.go(-1);
          }
        } catch (error) {
          Toast.clear();
        } finally {
          this.loading = false;
        }
      });
    },
  },
};
</script>

<style scoped lang="less">
.app {
  flex: 1;
  width: 100%;
  height: 100vh;
  background-color: var(--page-background);
}

.bank-account-content {
  padding: calc(14rem / 16) calc(14rem / 16) calc(36rem / 16);
}

.bank-account-fields {
  display: flex;
  flex-direction: column;
}

.field-label {
  margin: 0 0 calc(3rem / 16);
  color: #121212;
  font-size: calc(16rem / 16);
  font-weight: 500;
  line-height: 1.25;
}

.bank-label,
.account-number-label {
  margin-top: calc(2rem / 16);
}

.form-control {
  box-sizing: border-box;
  border: 1px solid #dedede;
  border-radius: calc(10rem / 16);
  background: #fff;
  color: #242424;
  font-size: calc(16rem / 16);
  outline: none;
  width: 100%;
  margin: 0 0 calc(2rem / 16);
  padding: calc(14rem / 16) calc(12rem / 16);

  &::placeholder {
    color: #8a8a8a;
    opacity: 1;
  }

  &:focus {
    border-color: var(--color-primary);
    box-shadow: var(--focus-shadow);
  }
}

.default-setting {
  display: flex;
  align-items: center;
  margin-top: calc(20rem / 16);
  padding: calc(16rem / 16) calc(14rem / 16);
  border: 0;
  border-radius: calc(8rem / 16);
  background: #fff;
  box-shadow: 0 calc(5rem / 16) calc(13rem / 16) rgba(0, 0, 0, 0.05);
  color: #303030;
  font-size: calc(15rem / 16);
  text-align: left;
}

.default-setting__check {
  display: flex;
  box-sizing: border-box;
  width: calc(22rem / 16);
  height: calc(22rem / 16);
  align-items: center;
  justify-content: center;
  margin-right: calc(10rem / 16);
  border: 2px solid #d7d7d7;
  border-radius: calc(3rem / 16);
  background: #fff;

  &.checked {
    border-color: var(--color-primary);
    background: var(--color-primary);
  }

  svg {
    width: calc(20rem / 16);
    height: calc(20rem / 16);
    fill: none;
    stroke: #fff;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.confirm-button {
  width: 100%;
  height: auto;
  margin-top: calc(24rem / 16);
  padding: calc(15rem / 16) calc(16rem / 16);
  border: 0;
  border-radius: calc(8rem / 16);
  background: var(--button-background);
  box-shadow: var(--card-shadow);
  color: var(--button-text);
  font-size: calc(20rem / 16);
  font-weight: 600;
  line-height: 1.35;

  &:active {
    opacity: 0.86;
  }

  &:disabled {
    opacity: 0.7;
  }
}

/deep/ .bank-account-nav.van-nav-bar,
/deep/ .bank-account-nav .van-nav-bar {
  background: var(--color-surface);
}

/deep/ .bank-account-nav .van-nav-bar__title,
/deep/ .bank-account-nav .van-icon {
  color: var(--color-text);
}

</style>
